<template>
    <div class="admin-dashboard">
        <div class="dash-header">
            <div class="dash-header-left">
                <div class="dash-title">{{ t('dashboard') }}</div>
                <div class="dash-subtitle">{{ t('welcome_admin') }} · {{ userName }}</div>
            </div>
            <div class="dash-header-actions">
                <button type="button" class="action-btn" @click="reloadAll" :disabled="reloading">{{ t('refresh') }}</button>
                <button type="button" class="action-btn action-btn-danger" @click="logout">{{ t('logout') }}</button>
            </div>
        </div>

        <div class="widget-grid">
            <button
                v-if="permissionsLoaded && canRegistration"
                type="button"
                class="widget widget-reg"
                @click="openRegistrationPage"
            >
                <div class="widget-top">
                    <div class="widget-label">{{ t('registration_queue') }}</div>
                    <div class="widget-count">{{ regTotal }}</div>
                </div>
                <div class="widget-bottom">
                    <div class="widget-chip">{{ t('pending') }}</div>
                    <div class="widget-cta">{{ t('open_page') }}</div>
                </div>
            </button>

            <button
                v-if="permissionsLoaded && canPayment"
                type="button"
                class="widget widget-pay"
                @click="openBillingPage"
            >
                <div class="widget-top">
                    <div class="widget-label">{{ t('customer_payment') }}</div>
                    <div class="widget-count">{{ payTotal }}</div>
                </div>
                <div class="widget-bottom">
                    <div class="widget-chip">{{ t('pending') }}</div>
                    <div class="widget-cta">{{ t('open_page') }}</div>
                </div>
            </button>

            <button type="button" class="widget widget-logout" @click="logout">
                <div class="widget-top">
                    <div class="widget-label">{{ t('logout') }}</div>
                    <div class="widget-count">×</div>
                </div>
                <div class="widget-bottom">
                    <div class="widget-chip">{{ t('dashboard') }}</div>
                    <div class="widget-cta">{{ t('logout') }}</div>
                </div>
            </button>
        </div>

        <div class="detail-grid">
            <div v-if="permissionsLoaded && canRegistration" class="panel-card">
                <div class="panel-head">
                    <div>
                        <div class="panel-title">{{ t('registration_queue') }}</div>
                        <div class="panel-subtitle">{{ t('pending') }}: {{ regTotal }}</div>
                    </div>
                    <button type="button" class="mini-btn" @click="openRegistrationPage">{{ t('open_page') }}</button>
                </div>

                <div v-if="regLoading" class="loading">{{ t('loading') }}</div>
                <div v-else-if="!regItem" class="empty">{{ t('no_data') }}</div>
                <div v-else class="detail-card">
                    <div class="detail-row">
                        <div class="detail-label">{{ t('customer_name') }}</div>
                        <div class="detail-value">{{ regItem.CustomerName || '-' }}</div>
                    </div>
                    <div class="detail-row">
                        <div class="detail-label">{{ t('member_type') }}</div>
                        <div class="detail-value">{{ regItem.MemberType || '-' }}</div>
                    </div>
                    <div class="detail-row">
                        <div class="detail-label">{{ t('category') }}</div>
                        <div class="detail-value">{{ regItem.MembershipCategory || '-' }}</div>
                    </div>
                    <div class="detail-row">
                        <div class="detail-label">{{ t('mobile') }}</div>
                        <div class="detail-value mono">{{ regItem.MobileNo || '-' }}</div>
                    </div>
                    <div class="detail-row">
                        <div class="detail-label">{{ t('district') }}</div>
                        <div class="detail-value">{{ regItem.District || '-' }}</div>
                    </div>

                    <div class="detail-actions">
                        <button type="button" class="btn btn-primary" @click="acceptCurrentRegistration" :disabled="regActionLoading">{{ t('accept') }}</button>
                        <button type="button" class="btn btn-secondary" @click="regPrev" :disabled="regLoading || regPage <= 1">{{ t('previous') }}</button>
                        <button type="button" class="btn btn-secondary" @click="regNext" :disabled="regLoading || regPage >= regTotalPages">{{ t('next') }}</button>
                    </div>
                </div>
            </div>

            <div v-if="permissionsLoaded && canPayment" class="panel-card">
                <div class="panel-head">
                    <div>
                        <div class="panel-title">{{ t('customer_payment') }}</div>
                        <div class="panel-subtitle">{{ t('pending') }}: {{ payTotal }}</div>
                    </div>
                    <button type="button" class="mini-btn" @click="openBillingPage">{{ t('open_page') }}</button>
                </div>

                <div v-if="payLoading" class="loading">{{ t('loading') }}</div>
                <div v-else-if="!payItem" class="empty">{{ t('no_data') }}</div>
                <div v-else class="detail-card">
                    <div class="detail-row">
                        <div class="detail-label">{{ t('customer') }}</div>
                        <div class="detail-value">{{ payItem.CustomerName || '-' }}</div>
                    </div>
                    <div class="detail-row">
                        <div class="detail-label">{{ t('bill_no') }}</div>
                        <div class="detail-value mono">{{ payItem.BillNo || '-' }}</div>
                    </div>
                    <div class="detail-row">
                        <div class="detail-label">{{ t('amount') }}</div>
                        <div class="detail-value mono">{{ payItem.Amount ?? '-' }}</div>
                    </div>
                    <div class="detail-row">
                        <div class="detail-label">{{ t('payment_method') }}</div>
                        <div class="detail-value">{{ payItem.Payment_Method || '-' }}</div>
                    </div>
                    <div class="detail-row">
                        <div class="detail-label">{{ t('trx') }}</div>
                        <div class="detail-value mono">{{ payItem.Transaction_ID || '-' }}</div>
                    </div>

                    <div class="detail-actions">
                        <button type="button" class="btn btn-primary" @click="confirmCurrentPayment" :disabled="payActionLoading">{{ t('confirm') }}</button>
                        <button type="button" class="btn btn-secondary" @click="payPrev" :disabled="payLoading || payPage <= 1">{{ t('previous') }}</button>
                        <button type="button" class="btn btn-secondary" @click="payNext" :disabled="payLoading || payPage >= payTotalPages">{{ t('next') }}</button>
                    </div>
                </div>
            </div>
        </div>

        <Teleport to="body">
            <div class="toast-container">
                <TransitionGroup name="toast" tag="div">
                    <div v-for="toast in toasts" :key="toast.id" :class="['toast', 'toast-' + toast.type]">
                        <div class="toast-text">{{ toast.message }}</div>
                        <button type="button" class="toast-close" @click="removeToast(toast.id)">×</button>
                    </div>
                </TransitionGroup>
            </div>
        </Teleport>
    </div>
</template>

<script>
import { ref, computed, onMounted, inject, Teleport, TransitionGroup } from 'vue';
import axios from 'axios';

export default {
    name: 'AdminDashboard',
    components: { Teleport, TransitionGroup },
    setup() {
        const adminT = inject('adminT', (k) => k);
        const t = (key) => adminT(key);

        const userName = ref(document.querySelector('meta[name="user-name"]')?.getAttribute('content') || 'Admin');
        const permissions = ref([]);
        const permissionsLoaded = ref(false);

        const regLoading = ref(false);
        const regActionLoading = ref(false);
        const regPage = ref(1);
        const regTotal = ref(0);
        const regItem = ref(null);

        const payLoading = ref(false);
        const payActionLoading = ref(false);
        const payPage = ref(1);
        const payTotal = ref(0);
        const payItem = ref(null);

        const reloading = ref(false);

        const toasts = ref([]);
        let toastSeq = 1;

        const addToast = (type, message) => {
            const id = toastSeq++;
            toasts.value.push({ id, type, message });
            window.setTimeout(() => removeToast(id), 3500);
        };

        const removeToast = (id) => {
            toasts.value = toasts.value.filter(t => t.id !== id);
        };

        const loadPermissions = async () => {
            try {
                const res = await axios.get('/admin/api/user-permissions');
                permissions.value = Array.isArray(res.data.permissions) ? res.data.permissions : [];
            } catch (e) {
                permissions.value = [];
            } finally {
                permissionsLoaded.value = true;
            }
        };

        const hasAnyPermission = (patterns) => {
            if (!permissionsLoaded.value) return false;
            if (permissions.value.includes('*')) return true;
            const list = Array.isArray(patterns) ? patterns : [patterns];
            return permissions.value.some(p => list.some(x => (p || '').toLowerCase() === (x || '').toLowerCase()));
        };

        const canRegistration = computed(() => hasAnyPermission(['view-registrations', 'approve-registrations']));
        const canPayment = computed(() => hasAnyPermission(['view-bill', 'submit-payment']));

        const regTotalPages = computed(() => Math.max(1, Math.ceil((regTotal.value || 0) / 1)));
        const payTotalPages = computed(() => Math.max(1, Math.ceil((payTotal.value || 0) / 1)));

        const loadRegistrationPage = async (page) => {
            if (!canRegistration.value) return;
            regLoading.value = true;
            try {
                const res = await axios.get('/admin/api/registration-queue', {
                    params: { status: 'pending', page, per_page: 1 },
                });
                const pagination = res.data?.data?.pagination || {};
                const items = res.data?.data?.items || [];
                regTotal.value = Number(pagination.total || 0);
                regPage.value = Number(pagination.page || page || 1);
                regItem.value = Array.isArray(items) && items.length ? items[0] : null;
            } catch (e) {
                regTotal.value = 0;
                regItem.value = null;
                addToast('error', e?.response?.data?.message || t('failed_to_load'));
            } finally {
                regLoading.value = false;
            }
        };

        const loadPaymentPage = async (page) => {
            if (!canPayment.value) return;
            payLoading.value = true;
            try {
                const res = await axios.get('/admin/api/payment-queue', {
                    params: { status: 'pending', page, per_page: 1 },
                });
                const pagination = res.data?.data?.pagination || {};
                const items = res.data?.data?.items || [];
                payTotal.value = Number(pagination.total || 0);
                payPage.value = Number(pagination.page || page || 1);
                payItem.value = Array.isArray(items) && items.length ? items[0] : null;
            } catch (e) {
                payTotal.value = 0;
                payItem.value = null;
                addToast('error', e?.response?.data?.message || t('failed_to_load'));
            } finally {
                payLoading.value = false;
            }
        };

        const regPrev = async () => {
            if (regPage.value <= 1) return;
            await loadRegistrationPage(regPage.value - 1);
        };

        const regNext = async () => {
            if (regPage.value >= regTotalPages.value) return;
            await loadRegistrationPage(regPage.value + 1);
        };

        const payPrev = async () => {
            if (payPage.value <= 1) return;
            await loadPaymentPage(payPage.value - 1);
        };

        const payNext = async () => {
            if (payPage.value >= payTotalPages.value) return;
            await loadPaymentPage(payPage.value + 1);
        };

        const acceptCurrentRegistration = async () => {
            const item = regItem.value;
            const id = item?.RegistrationQueueID;
            if (!id) return;

            regActionLoading.value = true;
            try {
                const res = await axios.post(`/admin/api/registration-queue/${id}/accept`);
                addToast('success', res.data?.message || t('success'));
                await loadRegistrationPage(1);
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                regActionLoading.value = false;
            }
        };

        const confirmCurrentPayment = async () => {
            const item = payItem.value;
            const id = item?.BillPaymentID;
            if (!id) return;

            payActionLoading.value = true;
            try {
                const res = await axios.post(`/admin/api/payment-queue/${id}/confirm`);
                addToast('success', res.data?.message || t('success'));
                await loadPaymentPage(1);
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                payActionLoading.value = false;
            }
        };

        const openRegistrationPage = () => {
            window.location.href = '/admin/registration-queue';
        };

        const openBillingPage = () => {
            window.location.href = '/admin/billing';
        };

        const reloadAll = async () => {
            reloading.value = true;
            try {
                await Promise.all([
                    canRegistration.value ? loadRegistrationPage(1) : Promise.resolve(),
                    canPayment.value ? loadPaymentPage(1) : Promise.resolve(),
                ]);
            } finally {
                reloading.value = false;
            }
        };

        const logout = () => {
            document.getElementById('logout-form')?.submit();
        };

        onMounted(async () => {
            await loadPermissions();
            await reloadAll();
        });

        return {
            t,
            userName,
            permissionsLoaded,
            canRegistration,
            canPayment,
            regLoading,
            regActionLoading,
            regPage,
            regTotal,
            regTotalPages,
            regItem,
            payLoading,
            payActionLoading,
            payPage,
            payTotal,
            payTotalPages,
            payItem,
            reloading,
            regPrev,
            regNext,
            payPrev,
            payNext,
            acceptCurrentRegistration,
            confirmCurrentPayment,
            openRegistrationPage,
            openBillingPage,
            reloadAll,
            logout,
            toasts,
            removeToast,
        };
    },
};
</script>

<style scoped>
.admin-dashboard {
    width: 100%;
}

.dash-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.09), rgba(16, 185, 129, 0.08));
    border: 1px solid var(--admin-border);
    border-radius: 18px;
    padding: 16px;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
}

.dash-title {
    font-size: 22px;
    font-weight: 900;
    color: var(--admin-text);
    line-height: 1.15;
}

.dash-subtitle {
    color: var(--admin-muted);
    font-weight: 700;
    font-size: 13px;
    margin-top: 4px;
}

.dash-header-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.action-btn {
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: rgba(255, 255, 255, 0.9);
    color: var(--admin-text);
    padding: 10px 12px;
    border-radius: 12px;
    font-weight: 800;
    cursor: pointer;
}

.action-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.action-btn-danger {
    background: rgba(239, 68, 68, 0.09);
    border-color: rgba(239, 68, 68, 0.22);
    color: #b91c1c;
}

.widget-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-top: 14px;
}

.widget {
    border: 1px solid rgba(15, 23, 42, 0.1);
    background: var(--admin-card-bg);
    border-radius: 18px;
    padding: 16px;
    cursor: pointer;
    text-align: left;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.06);
    transition: transform 0.16s ease, box-shadow 0.16s ease;
}

.widget:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 34px rgba(15, 23, 42, 0.09);
}

.widget-reg {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(255, 255, 255, 0.9));
}

.widget-pay {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(255, 255, 255, 0.9));
}

.widget-logout {
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(255, 255, 255, 0.9));
}

.widget-top {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
}

.widget-label {
    font-weight: 900;
    color: var(--admin-text);
    font-size: 14px;
}

.widget-count {
    font-weight: 950;
    color: var(--admin-text);
    font-size: 28px;
    line-height: 1;
}

.widget-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.widget-chip {
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(15, 23, 42, 0.1);
    background: rgba(255, 255, 255, 0.75);
    color: var(--admin-muted);
    font-size: 12px;
    font-weight: 800;
}

.widget-cta {
    color: var(--admin-text);
    font-weight: 900;
    font-size: 12px;
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
    margin-top: 14px;
}

.panel-card {
    background: var(--admin-card-bg);
    border: 1px solid var(--admin-border);
    border-radius: 18px;
    padding: 18px;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
}

.panel-title {
    font-size: 20px;
    font-weight: 800;
    color: var(--admin-text);
    margin-bottom: 4px;
}

.panel-subtitle {
    color: var(--admin-muted);
    font-size: 13px;
    margin-bottom: 16px;
}

.panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;
}

.mini-btn {
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: rgba(255, 255, 255, 0.9);
    color: var(--admin-text);
    padding: 8px 10px;
    border-radius: 12px;
    font-weight: 700;
    cursor: pointer;
}

.btn {
    border: none;
    border-radius: 12px;
    padding: 10px 14px;
    font-weight: 700;
    cursor: pointer;
}

.btn-primary {
    background: rgba(15, 23, 42, 0.92);
    color: #ffffff;
}

.btn-secondary {
    background: rgba(100, 116, 139, 0.12);
    color: var(--admin-text);
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.detail-card {
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: var(--admin-soft-bg);
    border-radius: 14px;
    padding: 14px;
}

.detail-row {
    display: grid;
    grid-template-columns: 130px 1fr;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-label {
    color: var(--admin-muted);
    font-size: 12px;
    font-weight: 700;
}

.detail-value {
    color: var(--admin-text);
    font-size: 13px;
    font-weight: 700;
}

.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.detail-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    padding-top: 14px;
}

.loading,
.empty {
    padding: 22px 12px;
    text-align: center;
    color: var(--admin-muted);
    font-weight: 700;
}

.toast-container {
    position: fixed;
    bottom: 18px;
    right: 18px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.toast {
    min-width: 260px;
    max-width: 380px;
    border-radius: 14px;
    padding: 12px 12px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.12);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.toast-success {
    border-color: rgba(16, 185, 129, 0.22);
}

.toast-error {
    border-color: rgba(239, 68, 68, 0.22);
}

.toast-text {
    font-weight: 700;
    color: var(--admin-text);
    font-size: 13px;
}

.toast-close {
    border: none;
    background: transparent;
    color: var(--admin-muted);
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
}

.toast-enter-active,
.toast-leave-active {
    transition: all 0.18s ease;
}

.toast-enter-from,
.toast-leave-to {
    opacity: 0;
    transform: translateY(6px);
}

@media (max-width: 1024px) {
    .widget-grid {
        grid-template-columns: 1fr;
    }

    .detail-grid {
        grid-template-columns: 1fr;
    }
}
</style>
